<script setup lang="ts">
import type { CourseRoleId } from '~/data/courseRoles'
import type { components } from '~/types/napis'
import type { TCreateJobApplicationPayload } from '~/types/job-application'

const { $sentryCaptureException } = useNuxtApp()

const props = defineProps({
  show: {
    type: Boolean,
    required: true,
    default: false
  },
  job: {
    type: Object as PropType<components['schemas']['JobResponseSchema']>,
    required: true
  }
})

const emits = defineEmits(['showPopup', 'closePopup'])

const { data: user } = useAuth()

const { createJobApplication } = useJobApplication()
const { uploadFileToS3 } = useAuthFetch()

// relevancy - temporary
const isChecking = ref(false)
// const relevancy = undefined
const relevancy = {
  level: 3, // 1 is low, 3 is high
  recommendations: [
    'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua',
    'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua'
  ]
}

// options
const workingPermitOptions = ['No need', 'Yes, I need a working permit']
const languageOptions = [
  'English',
  'Malay',
  'Mandarin',
  'Cantonese',
  'Japanese',
  'Korean',
  'Arabic',
  'French',
  'Tamil'
]

// forms
const formId = 'job-apply-form'
type FormValues = {
  firstName: string
  lastName: string
  email: string
  phone?: {
    value: ''
    isValid: boolean
  }
  workingPermit: string
  languages: string[]
  resume?: File
}
const formValues = ref({
  firstName: user.value?.first_name || '',
  lastName: user.value?.last_name || '',
  email: user.value?.email || '',
  phone: {
    value: user.value?.secret_part?.phone || '',
    isValid: false
  },
  workingPermit: '',
  languages: [],
  resume: undefined
})
const formErrors = ref<string[]>([])

async function submitHandler(submittedData: FormValues) {
  if (!user.value || !submittedData.resume) return

  formErrors.value = []
  // Upload file to S3 for job application
  let fileIdSlug: string | null
  try {
    fileIdSlug = await uploadFileToS3(
      `/api/jobs/${props.job.id_slug}/applications/files/`,
      submittedData.resume,
      {
        file_type: 'RESUME'
      }
    )
  } catch (error) {
    handleFetchError(error, {
      handleUnknownError: () => {
        formErrors.value = ['Something went wrong. Please try again.']
        $sentryCaptureException(error)
      },
      handle400Error: () => {
        formErrors.value = ['Invalid Inputs.']
      },
      handle500OrAboveError: () => {
        formErrors.value = ['Something went wrong. Please try again.']
        $sentryCaptureException(error)
      }
    })
    return
  }

  if (!fileIdSlug) return

  const payload: TCreateJobApplicationPayload = {
    applicant_id_slug: user.value.id_slug,
    resume_id_slug: fileIdSlug,
    first_name: submittedData.firstName,
    last_name: submittedData.lastName,
    email: submittedData.email,
    phone: submittedData.phone?.value,
    notify_user: false,
    notify_hr: false,
    visa_status: submittedData.workingPermit === 'No need' ? 'NO_NEED' : 'NEED_A_PERMIT',
    languages: submittedData.languages,
    allow_upsert: true,
  }

  console.log('payload', payload)

  try {
    await createJobApplication(props.job.id_slug as string, payload)
    emits('closePopup')
  } catch (error) {
    handleFetchError(error, {
      handleUnknownError: () => {
        formErrors.value = ['Something went wrong. Please try again.']
        $sentryCaptureException(error)
      },
      handle400Error: (_error) => {
        formErrors.value = ['Invalid Inputs.']
      },
      handle500OrAboveError: () => {
        formErrors.value = ['Something went wrong. Please try again.']
        $sentryCaptureException(error)
      }
    })
  }
}
</script>

<template>
  <CommonPopupScreen
    :show="show"
    disable-tap-background
    @show-popup="emits('showPopup')"
    @close-popup="emits('closePopup')"
  >
    <div class="job-apply hide-scrollbar">
      <div class="job-apply-header">
        <h1>{{ props.job.title }}</h1>

        <div class="job-apply-header-employer">
          <img
            :src="toCompanyIconUrl(props.job.company_part?.logo_part?.object_key)"
            :alt="props.job.company_part?.name || 'Company Logo'"
          >

          <span>{{ props.job.company_part?.name }}</span>
        </div>

        <div class="job-apply-header-summary">
          <div v-if="props.job.location" class="job-apply-header-summary-item location">
            <CommonIconJobsIconLocationOutline />
            <span>{{ getReadableJobLocation(props.job.location) }}</span>
          </div>

          <div v-if="props.job.mode" class="job-apply-header-summary-item">
            <CommonIconJobsIconBriefcase />
            <span>{{ getReadableJobWorkMode(props.job.mode) }}</span>
          </div>

          <div v-if="props.job.work_mode" class="job-apply-header-summary-item">
            <CommonIconJobsIconBuilding />
            <span>{{ getReadableJobWorkMode(props.job.work_mode) }}</span>
          </div>

          <div v-if="props.job.role" class="job-apply-header-summary-item">
            <CommonIconJobsIconSpanner />
            <span>{{ getReadableJobRole(props.job.role as CourseRoleId) }}</span>
          </div>

          <div v-if="props.job.posting_date" class="job-apply-header-summary-item">
            <CommonIconJobsIconTime />
            <span>{{ getReadableDayDifferenceFromToday(props.job.posting_date) }}</span>
          </div>
        </div>
      </div>

      <h6>Please fill in the form below to apply:</h6>
      <FormKit
        :id="formId"
        v-model="formValues"
        type="form"
        submit-label="Login"
        :actions="false"
        :errors="formErrors"
        @submit="submitHandler"
      >
        <div class="two-col">
          <FormKit
            type="text"
            name="firstName"
            label="First Name"
            placeholder="John"
            validation="required"
            validation-visibility="dirty"
            autocomplete="given-name"
            autofocus
          />

          <FormKit
            type="text"
            name="lastName"
            label="Last Name"
            placeholder="Doe"
            validation="required"
            validation-visibility="dirty"
            autocomplete="family-name"
          />

          <FormKit
            type="email"
            name="email"
            label="Email"
            disabled
            placeholder="your@email.com"
            validation="required|email"
            validation-visibility="dirty"
            autocomplete="email,username"
          />

          <FormKit
            type="tlPhone"
            name="phone"
            label="Phone number"
            placeholder="123456789"
            validation="required|requiredPhone|tlPhone"
            validation-visibility="dirty"
          />

          <div class="input">
            <label>Languages?</label>
            <FormKit
              type="tlDropdown"
              name="languages"
              placeholder="Choose languages"
              :item-options="languageOptions"
              :multiple="true"
              values-title="Languages"
              validation="required"
              validation-visibility="dirty"
            />
          </div>

          <div class="input">
            <label>Need a working permit?</label>
            <FormKit
              type="tlDropdown"
              name="workingPermit"
              placeholder="Choose working permit"
              :item-options="workingPermitOptions"
              :value="formValues.workingPermit"
              :multiple="false"
              validation="required"
              validation-visibility="dirty"
            />
          </div>
        </div>

        <div class="one-col">
          <div class="input">
            <h6>Please upload your resume:</h6>

            <!-- Relevancy section -->
            <!-- <div v-if="formValues.resume" id="relevancy-container"> -->
            <div v-if="false" id="relevancy-container">
              <div v-if="isChecking" id="relevancy-checking">
                <span>
                  We are currently calculating the relevancy score of your resume.
                  <br >
                  It will take a few seconds...
                </span>

                <div id="relevancy-checking-bar">
                  <div id="relevancy-checking-inner">
                    <div id="relevancy-checking-moving" />
                  </div>
                </div>
              </div>
              <div v-else-if="relevancy" id="relevancy-content">
                <div
                  id="relevancy-content-header"
                  :class="`relevant-${relevancy.level === 3 ? 'green' : relevancy.level === 2 ? 'blue' : 'red'}`"
                >
                  <span>Relevancy to the job:</span>

                  <div id="relevancy-content-header-level">
                    <h5 v-if="relevancy.level === 3">Highly relevant</h5>
                    <h5 v-else-if="relevancy.level === 2">Normal relevancy</h5>
                    <h5 v-else>Irrelevant</h5>

                    <div id="relevancy-content-header-stars">
                      <CommonIconStar
                        v-for="i in 3"
                        :key="`relevant-star-${i}`"
                        :class="i <= relevancy.level ? 'star-yellow' : 'star-grey'"
                      />
                    </div>
                  </div>
                </div>

                <div id="relevancy-recommendations">
                  <h4>Our recommendations:</h4>
                  <div
                    v-for="rec in relevancy.recommendations"
                    :key="rec"
                    class="recommendation-item"
                  >
                    <PageCoursesCourseCheckpoint :title="rec" />
                  </div>
                </div>
              </div>
            </div>

            <FormKit
              type="tlDoc"
              name="resume"
              accept=".pdf"
              validation="required"
              validation-visibility="dirty"
            />
          </div>

          <div class="submit">
            <FormKit type="submit" label="Submit application"/>
          </div>
        </div>
      </FormKit>
    </div>
  </CommonPopupScreen>
</template>

<style lang="scss" scoped>
.job-apply {
  height: 60dvh;
  width: 900px;
  overflow-y: scroll;
  padding: 20px 0 20px;

  h6 {
    font-size: 0.95rem;
    font-weight: 500;
    margin: 35px 0 45px;
    display: block;
    position: relative;
  }

  h6::before {
    content: '';
    position: absolute;
    bottom: -7px;
    left: 0;
    background: var(--purple);
    height: 5px;
    width: 30px;
  }

  &-header {
    border-bottom: 1px solid gainsboro;
    padding: 5px 0 30px;

    h1 {
      font-size: 1.3rem;
      line-height: 1.8rem;
    }

    &-employer {
      display: flex;
      align-items: center;
      margin: 12px 0 17px;
      font-size: 0.95rem;

      img {
        $imgSize: 27px;
        height: $imgSize;
        width: $imgSize;
        border-radius: 50%;
        border: 1px solid gainsboro;
        margin-right: 10px;
      }
    }

    &-summary {
      display: flex;
      flex-wrap: wrap;
      column-gap: 40px;
      row-gap: 10px;
      color: var(--textMedium);
      font-size: 0.9rem;

      .location {
        width: 100%;
      }

      &-item {
        svg {
          $iconSize: 12px;
          height: $iconSize;
          width: $iconSize;
          margin-right: 10px;
          fill: var(--textMedium);
        }
      }
    }
  }

  &-resume {
    h6 {
      margin-bottom: 25px;
    }
  }

  &-btn {
    display: flex;
    justify-content: center;
    padding-top: 30px;
  }
}

form {
  border-bottom: 1px solid gainsboro;
  padding-bottom: 40px;
  text-align: left;

  .two-col {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 20px;
    row-gap: 40px;
  }

  .one-col {
    display: grid;
    grid-template-columns: 1fr;
    row-gap: 40px;
  }

  :deep(.dropdown) {
    width: 100%;
  }

  .submit {
    margin: 0;
    text-align: center;
  }

  .input {
    margin-bottom: 0;
  }
}

#relevancy {
  &-checking {
    margin: -10px 0 30px;

    &-bar {
      margin-top: 17px;
      height: 8px;
      border-radius: 20px;
      background: rgb(239, 239, 239);
      overflow: hidden;
    }

    &-inner {
      height: 100%;
      width: 100%;
      animation: moving 2s linear infinite;
    }

    &-moving {
      height: 100%;
      width: 120px;
      background-color: var(--purple);
      border-radius: 20px;
    }
  }

  &-content {
    border: 1px solid gainsboro;
    border-radius: 10px;
    overflow: hidden;
    margin: -10px 0 25px;

    &-header {
      padding: 17px 25px;
      display: flex;
      justify-content: space-between;

      span {
        font-size: 0.95rem;
      }

      h5 {
        font-size: 0.97rem;
        margin-right: 10px;
      }

      &-level {
        display: flex;
        align-items: center;

        svg {
          $starSize: 15px;
          height: $starSize;
          width: $starSize;
          margin-left: 5px;
        }

        .star-yellow {
          fill: rgb(254, 182, 48);
        }

        .star-grey {
          fill: rgb(199, 198, 198);
        }
      }
    }

    .relevant {
      &-green {
        background-color: rgb(214, 242, 242);

        h5 {
          color: rgb(3, 165, 165);
        }
      }

      &-blue {
        background-color: rgb(224, 239, 246);

        h5 {
          color: var(--blue);
        }
      }

      &-red {
        background-color: rgb(254, 228, 224);

        h5 {
          color: rgb(206, 61, 45);
        }
      }
    }
  }

  &-recommendations {
    padding: 25px 25px 30px;
    display: flex;
    flex-direction: column;
    gap: 15px;

    h4 {
      font-size: 0.95rem;
    }
  }
}

@media (max-width: 1100px) {
  .job-apply {
    width: 100%;
  }
}

@media (max-width: 900px) {
  .job-apply {
    height: auto;
  }
}

@media (max-width: 700px) {
  form {
    grid-template-columns: 1fr;
    row-gap: 32px;

    .two-col {
      grid-template-columns: 1fr;
      gap: 32px;
    }
  }

  #relevancy {
    &-container {
      margin-right: -20px;
      margin-left: -20px;
    }

    &-checking {
      margin: -5px 0 35px;
    }

    &-content {
      margin: -5px 0 35px;
      border-radius: 0;

      &-header {
        padding: 20px;
        flex-direction: column;
        gap: 3px;
      }
    }

    &-recommendations {
      padding: 25px 20px 30px;

      :deep(.check-point) {
        span {
          font-size: 1rem;
          line-height: 1.5rem;
        }
      }
    }
  }
}

@keyframes moving {
  0% {
    transform: translateX(-120px);
  }
  100% {
    transform: translateX(100%);
  }
}
</style>
